import {userAgentFromString} from "next/server";

/**
 * Headless browsers and HTTP client libraries used by scrapers/automation.
 * Next's built-in check (https://nextjs.org/docs/app/api-reference/functions/userAgent)
 * only covers named crawlers, not these — unlike a crawler list, these tool
 * names rarely change, so this needs little upkeep.
 */
const AUTOMATION_TOOL_PATTERN =
	/headless|phantomjs|selenium|puppeteer|playwright|curl|wget|python-requests|axios|go-http-client|okhttp|postmanruntime/i;

export type BotReason = "missing_user_agent" | "known_bot" | "automation_tool";

/**
 * Like `isBot`, but reports which check matched. Missing/empty UA is
 * currently treated as a bot by default — this is unconfirmed and under
 * investigation (WEL-1451/1452 follow-up), since it may be misclassifying
 * real traffic. Callers should log the reason at their block point so we
 * can see real traffic before changing that default.
 */
export function getBotReason(
	userAgent: string | null | undefined
): BotReason | null {
	if (!userAgent) {
		return "missing_user_agent";
	}
	if (userAgentFromString(userAgent).isBot) {
		return "known_bot";
	}
	if (AUTOMATION_TOOL_PATTERN.test(userAgent)) {
		return "automation_tool";
	}
	return null;
}

export function isBot(userAgent: string | null | undefined): boolean {
	return getBotReason(userAgent) !== null;
}

/**
 * `known_bot` (named crawlers) and `automation_tool` (headless
 * browsers/HTTP clients) are unambiguous — never real user traffic, so they
 * stay fully blocked. Only `missing_user_agent` is in shadow mode: it's the
 * one reason that could be misclassifying real users (a stripped header,
 * not necessarily a bot), so it's tagged instead of blocked until we have
 * data. See getBotReason's doc comment.
 */
export function isConfirmedBot(reason: BotReason | null): boolean {
	return reason === "known_bot" || reason === "automation_tool";
}
